import React from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import SFSkeleton from "../skeleton";
//import { isResponsiveSize } from "../responsive";

//xs={12} md={6} xl={3}

export const SFGrid = ({
  variant = 4,
  spacing = 3,
  direction = "row",
  children,
  alignItems = "stretch",
  skeleton = false,
  count = 12,
}) => {
  const gridVariantsMappings = {
    2: {
      columns: 12,
      xs: 12 / 1,
      sm: 12 / 1,
      md: 12 / 1,
      lg: 12 / 2,
      xl: 12 / 2,
      xxl: 12 / 2,
    },
    3: {
      columns: 12,
      xs: 12 / 1,
      sm: 12 / 1,
      md: 12 / 2,
      lg: 12 / 3,
      xl: 12 / 3,
      xxl: 12 / 3,
    },
    4: {
      columns: 12,
      xs: 12 / 1,
      sm: 12 / 1,
      md: 12 / 2,
      lg: 12 / 3,
      xl: 12 / 4,
      xxl: 12 / 4,
    },
    5: {
      columns: 15,
      xs: 15 / 1,
      sm: 15 / 1,
      md: 15 / 2,
      lg: 15 / 3,
      xl: 15 / 4,
      xxl: 15 / 5,
    },
  };

  if (skeleton) {
    return (
      <Grid
        container
        columns={gridVariantsMappings[variant].columns}
        direction={direction}
        spacing={spacing}
        alignItems={alignItems}
      >
        {[...Array(count)].map((i, index) => (
          <Grid
            item
            key={index}
            xs={gridVariantsMappings[variant].xs}
            sm={gridVariantsMappings[variant].sm}
            md={gridVariantsMappings[variant].md}
            lg={gridVariantsMappings[variant].lg}
            xl={gridVariantsMappings[variant].xl}
            xxl={gridVariantsMappings[variant].xxl}
          >
            <SFSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }
  return (
    <Grid
      container
      columns={gridVariantsMappings[variant].columns}
      direction={direction}
      spacing={spacing}
      alignItems={alignItems}
    >
      {children.map((child, idx) => (
        <Grid
          item
          key={idx}
          xs={gridVariantsMappings[variant].xs}
          sm={gridVariantsMappings[variant].sm}
          md={gridVariantsMappings[variant].md}
          lg={gridVariantsMappings[variant].lg}
          xl={gridVariantsMappings[variant].xl}
          xxl={gridVariantsMappings[variant].xxl}
        >
          {child}
        </Grid>
      ))}
    </Grid>
  );
};

SFGrid.propTypes = {
  variant: PropTypes.oneOf([3, 4, 5]),
  spacing: PropTypes.number,
  direction: PropTypes.oneOf(["row", "column"]),
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  alignItems: PropTypes.string,
  skeleton: PropTypes.bool,
  count: PropTypes.number,
};

export const SFGridContainer = ({
  direction = "row",
  spacing = 3,
  justifyContent = "flex-start",
  alignItems = "flex-start",
  columnSpacing = null,
  rowSpacing = null,
  children,
  sx = {},
  columns = 12,
  className = null,
  maxWidth = null,
}) => (
  <Grid
    columns={columns}
    direction={direction}
    spacing={spacing}
    justifyContent={justifyContent}
    alignItems={alignItems}
    container
    rowSpacing={rowSpacing}
    columnSpacing={columnSpacing}
    sx={sx}
    className={className}
    maxWidth={maxWidth}
  >
    {children}
  </Grid>
);

SFGridContainer.propTypes = {
  spacing: PropTypes.number,
  direction: PropTypes.oneOfType([
    PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
    PropTypes.object,
  ]),
  justifyContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "flex-end",
      "center",
      "space-between",
      "space-around",
      "space-evenly",
      "initial",
      "inherit",
    ]),
    PropTypes.object,
  ]),
  alignItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "stretch",
      "center",
      "flex-start",
      "flex-end",
      "baseline",
      "initial",
      "inherit",
    ]),
    PropTypes.object,
  ]),
  rowSpacing: PropTypes.number,
  columnSpacing: PropTypes.number,
  columns: PropTypes.number,
  className: PropTypes.string,
};

export const SFGridItem = ({
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  children,
  sx = {},
  className = null,
}) => {
  const display = {
    xs: xs === false ? "none" : "block",
    sm: sm === false ? "none" : "block",
    md: md === false ? "none" : "block",
    lg: lg === false ? "none" : "block",
    xl: xl === false ? "none" : "block",
    xxl: xxl === false ? "none" : "block",
  };

  return (
    <Grid
      item
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      xxl={xxl}
      sx={{
        ...sx,
        ...(xs === false && {
          display,
        }),
      }}
      className={className}
    >
      {children}
    </Grid>
  );
};

SFGridItem.propTypes = {
  xs: PropTypes.oneOf([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    20,
    "auto",
    true,
    false,
  ]),
  sm: PropTypes.oneOf([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    "auto",
    true,
    false,
  ]),
  md: PropTypes.oneOf([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    "auto",
    true,
    false,
  ]),
  lg: PropTypes.oneOf([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    "auto",
    true,
    false,
  ]),
  xl: PropTypes.oneOf([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    "auto",
    true,
    false,
  ]),
  xxl: PropTypes.oneOf([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    "auto",
    true,
    false,
  ]),
  sx: PropTypes.object,
  className: PropTypes.string,
};

export const SFGridContainerItem = ({
  direction = "row",
  spacing = 3,
  justifyContent = "flex-start",
  alignItems = "flex-start",
  rowSpacing = null,
  columnSpacing = null,
  children,
  sx = {},
  className = null,
}) => (
  <Grid
    columns={12}
    spacing={spacing}
    justifyContent={justifyContent}
    alignItems={alignItems}
    container
    item
    rowSpacing={rowSpacing}
    columnSpacing={columnSpacing}
    direction={direction}
    sx={sx}
    className={className}
  >
    {children}
  </Grid>
);

SFGridContainerItem.propTypes = {
  spacing: PropTypes.number,
  columns: PropTypes.number,
  direction: PropTypes.oneOf([
    "row",
    "row-reverse",
    "column",
    "column-reverse",
  ]),
  justifyContent: PropTypes.oneOf([
    "flex-start",
    "flex-end",
    "center",
    "space-between",
    "space-around",
    "space-evenly",
    "initial",
    "inherit",
  ]),
  alignItems: PropTypes.oneOf([
    "stretch",
    "center",
    "flex-start",
    "flex-end",
    "baseline",
    "initial",
    "inherit",
  ]),
  bottomGutter: PropTypes.oneOf([0, 1, 2, 3]),
  sx: PropTypes.object,
};

export const SFStack = ({
  children,
  spacing,
  direction = "column",
  alignItems,
  justifyContent,
  ...props
}) => {
  return (
    <Stack
      alignItems={alignItems}
      justifyContent={justifyContent}
      direction={direction}
      spacing={spacing}
      {...props}
    >
      {children}
    </Stack>
  );
};

SFStack.propTypes = {
  direction: PropTypes.oneOfType([
    PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
    PropTypes.object,
  ]),
  children: PropTypes.node,
  spacing: PropTypes.number,
  alignItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "center",
      "flex-end",
      "stretch",
      "baseline",
    ]),
    PropTypes.object,
  ]),
  justifyContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "center",
      "flex-end",
      "space-between",
      "space-around",
      "space-evenly",
    ]),
    PropTypes.object,
  ]),
};

export const SFInternalStack = ({ children, ...props }) => {
  return <Stack {...props}>{children}</Stack>;
};
